import { sgsHolidayFieldSuffix } from "./sgs-holidays";

export const SHIP_BY_WEEK_SHORTCUTS = [1, 4, 8, 12, 16] as const;

export const SHIP_BY_DATE_BEFORE_TODAY_MESSAGE = "Ship-by date cannot be before today.";

export function chicagoTodayIsoDate(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(now);
}

/** True when the ship-by field is unchanged, empty, or today or later. */
export function shipByDateWriteIsAllowed(nextShipByDate: string, storedShipByDate: string, today: string): boolean {
  const next = nextShipByDate.trim().slice(0, 10);
  if (next === "") {
    return true;
  }
  const stored = storedShipByDate.trim().slice(0, 10);
  if (stored !== "" && next === stored) {
    return true;
  }
  return next >= today;
}

export function formatIsoDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${String(year)}-${month}-${day}`;
}

export function parseIsoDate(value: string): Date | null {
  const trimmed = value.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return null;
  }
  const parsed = new Date(`${trimmed}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatUsDateFromIso(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const parsed = parseIsoDate(value);
  if (parsed == null) {
    return value;
  }
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${month}/${day}/${String(parsed.getFullYear())}`;
}

export function shipByDateFromWeeksFromToday(weeks: number, baseDate = new Date()): string {
  const next = new Date(baseDate);
  next.setDate(next.getDate() + weeks * 7);
  const day = next.getDay();
  if (day === 6) {
    next.setDate(next.getDate() + 2);
  } else if (day === 0) {
    next.setDate(next.getDate() + 1);
  }
  return formatIsoDate(next);
}

export function shipByWeeksFromNowLabel(isoDate: string): string | null {
  if (isoDate.trim() === "") {
    return null;
  }
  const target = parseIsoDate(isoDate);
  if (target == null) {
    return null;
  }
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const then = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const diffDays = Math.round((then.getTime() - today.getTime()) / 86400000);
  const weeks = diffDays / 7;
  return `${weeks.toFixed(1)} week${Math.abs(weeks) === 1 ? "" : "s"} from now`;
}

export function shipByDateFieldHelper(isoDate: string): string | null {
  if (isoDate.trim() === "") {
    return null;
  }
  const weeksLabel = shipByWeeksFromNowLabel(isoDate);
  const holidayLabel = sgsHolidayFieldSuffix(isoDate);
  if (weeksLabel == null && holidayLabel == null) {
    return null;
  }
  if (weeksLabel == null) {
    return holidayLabel;
  }
  if (holidayLabel == null) {
    return weeksLabel;
  }
  return `${weeksLabel} · ${holidayLabel}`;
}

export function monthMatrix(year: number, monthIndex: number): Array<{ isoDate: string; inMonth: boolean }> {
  const firstOfMonth = new Date(year, monthIndex, 1, 12, 0, 0, 0);
  const startOffset = firstOfMonth.getDay();
  const gridStart = new Date(year, monthIndex, 1 - startOffset, 12, 0, 0, 0);
  const cells: Array<{ isoDate: string; inMonth: boolean }> = [];
  for (let index = 0; index < 42; index += 1) {
    const cellDate = new Date(gridStart);
    cellDate.setDate(gridStart.getDate() + index);
    cells.push({
      isoDate: formatIsoDate(cellDate),
      inMonth: cellDate.getMonth() === monthIndex,
    });
  }
  return cells;
}

export function monthMatrixWeekRows(
  year: number,
  monthIndex: number,
): Array<Array<{ isoDate: string; inMonth: boolean }>> {
  const cells = monthMatrix(year, monthIndex);
  const rows: Array<Array<{ isoDate: string; inMonth: boolean }>> = [];
  for (let index = 0; index < cells.length; index += 7) {
    rows.push(cells.slice(index, index + 7));
  }
  return rows;
}
